import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import InventoryItemGrid from "./InventoryItemGrid";
import InventoryItemList from "./InventoryItemList";
import TextField from "../../../../common/components/display/TextField/TextField";
import InventoryItemEditorModal from "../../../../common/components/modals/InventoryItemEditorModal/InventoryItemEditorModal";
import { useCallback, useRef, useState } from "react";
import InventoryItem from "../../../../utils/game/mechanics/InventoryItem";

import listIcon from "../../../../assets/images/common/svg/list.svg";
import gridIcon from "../../../../assets/images/common/svg/grid.svg";
import plusIcon from "../../../../assets/images/common/svg/plus.svg";

import "./style/InventoryItemDynamicCollection_style.css";
import { UIInventoryItemCollectionProps } from "./InventoryItemCollection";

export type DynamicInventoryDisplayMode = "list" | "grid";

type Props = UIInventoryItemCollectionProps & {
    label?: string;
    displayMode?: DynamicInventoryDisplayMode;
    onDisplayModeChanged?: (mode: DynamicInventoryDisplayMode) => void;
    expanded?: boolean;
    onExpandedChanged?: (val: boolean) => void;
    contentAreaClassName?: string;
    onItemChanged?: (
        item: InventoryItem | null,
        index: number,
        mode: "insert" | "replace",
        isInSequence: boolean
    ) => void;
    onItemsReordered?: (reorderedItems: Array<InventoryItem>) => void;
    onTransferSequenceComplete?: () => void;
};

export default function InventoryItemDynamicCollection({
    id,
    items,
    isEditable,
    label,
    displayMode,
    itemRestrictor,
    onDisplayModeChanged,
    expanded,
    onExpandedChanged,
    contentAreaClassName,
    onItemChanged,
    onItemsReordered,
    onTransferSequenceComplete,
}: Props): JSX.Element {
    const [activeDisplayMode, setActiveDisplayMode] =
        useState<DynamicInventoryDisplayMode>(displayMode ?? "list");
    const [isItemEditorVisible, setItemEditorVisibility] =
        useState<boolean>(false);
    const [itemEditorIndex, setItemEditorIndex] = useState<number | null>(null);
    const [itemEditorInitialData, setItemEditorInitialData] = useState<
        InventoryItem | undefined
    >(undefined);

    const ref = useRef<HTMLDivElement>(null);

    const onItemEditRequested = useCallback(
        (itemIndex: number) => {
            if (itemIndex >= 0 && itemIndex < items.length) {
                setItemEditorIndex(itemIndex);
                setItemEditorInitialData(items[itemIndex]);
                setItemEditorVisibility(true);
            }
        },
        [items, setItemEditorIndex, setItemEditorVisibility]
    );

    return (
        <div ref={ref} className="inventory-item-dynamic-collection-container">
            <FlexColumn>
                <FlexRow
                    className="header"
                    onClick={(e) => {
                        onExpandedChanged?.(!expanded === true);
                        e?.stopPropagation();
                    }}
                >
                    <TextField type="bold" className="header-text">
                        {label ?? ""}
                    </TextField>
                    <FlexRow className="toolbar" alignment="end">
                        <button
                            className="toolbar-button"
                            onClick={(e) => {
                                setActiveDisplayMode(() => {
                                    onDisplayModeChanged?.("list");
                                    return "list";
                                });
                                onExpandedChanged?.(true);
                                e.stopPropagation();
                            }}
                            style={{
                                backgroundColor:
                                    displayMode === "list"
                                        ? "darkgray"
                                        : undefined,
                            }}
                        >
                            <img src={listIcon} alt="List" />
                        </button>
                        <button
                            className="toolbar-button"
                            onClick={(e) => {
                                setActiveDisplayMode(() => {
                                    onDisplayModeChanged?.("grid");
                                    return "grid";
                                });
                                onExpandedChanged?.(true);
                                e.stopPropagation();
                            }}
                            style={{
                                backgroundColor:
                                    displayMode === "grid"
                                        ? "darkgray"
                                        : undefined,
                            }}
                        >
                            <img src={gridIcon} alt="Grid" />
                        </button>
                        {isEditable && (
                            <>
                                <div className="button-spacer" />
                                <button
                                    className="toolbar-button"
                                    onClick={(e) => {
                                        setItemEditorVisibility(true);
                                        e.stopPropagation();
                                    }}
                                >
                                    <img src={plusIcon} alt="+" />
                                </button>
                            </>
                        )}
                    </FlexRow>
                </FlexRow>
                <div
                    className={`contents-wrapper${
                        expanded === true ? `` : ` hidden`
                    }${
                        contentAreaClassName == null
                            ? ""
                            : ` ${contentAreaClassName}`
                    }`}
                >
                    {activeDisplayMode === "grid" && (
                        <InventoryItemGrid
                            id={id}
                            items={items}
                            isEditable={isEditable}
                            itemRestrictor={itemRestrictor}
                            onItemEditRequested={onItemEditRequested}
                            onItemChanged={onItemChanged}
                            onItemsReordered={onItemsReordered}
                            onTransferSequenceComplete={
                                onTransferSequenceComplete
                            }
                        />
                    )}
                    {activeDisplayMode === "list" && (
                        <InventoryItemList
                            id={id}
                            items={items}
                            isEditable={isEditable}
                            itemRestrictor={itemRestrictor}
                            onItemEditRequested={onItemEditRequested}
                            onItemChanged={onItemChanged}
                            onItemsReordered={onItemsReordered}
                            onTransferSequenceComplete={
                                onTransferSequenceComplete
                            }
                        />
                    )}
                </div>
            </FlexColumn>
            {isItemEditorVisible && (
                <InventoryItemEditorModal
                    isVisible={isItemEditorVisible}
                    onClose={() => {
                        setItemEditorVisibility(false);
                        setItemEditorInitialData(undefined);
                    }}
                    onConfirm={(itemData) => {
                        const mode =
                            itemEditorIndex == null ? "insert" : "replace";
                        onItemChanged?.(
                            itemData,
                            itemEditorIndex ?? items.length,
                            mode,
                            false
                        );
                        setItemEditorVisibility(false);
                        setItemEditorInitialData(undefined);
                    }}
                    initialItemData={itemEditorInitialData}
                />
            )}
        </div>
    );
}
